<template>
  <div>
    <page-header
      :title="$t('recipes')"
      :subtitle="$t('manage_recipes')"
      icon="mdi-book-open-page-variant"
    >
      <template #actions v-if="!device.isMobile">
        <circle-button
          :loading="loading.load"
          @click="load"
          icon="mdi-refresh"
          color="primary"
          :small="false"
          class="mr-2"
        />
        <global-button-print @click="print" :disabled="!items.length" />
        <global-button-excel @click="exportExcel" :disabled="!items.length" />
      </template>
    </page-header>

    <page-toolbar :searchable="false" :loading="loading.load" v-if="!device.isMobile">
      <template #filters>
        <v-col cols="auto" lg="3">
          <dropdown
            :label="$t('view')"
            :items="showItems"
            v-model="selectedLayout"
            hide-details
            color="white"
          />
        </v-col>
        <v-col cols="auto" lg="4">
          <date-picker
            v-model="dateRange"
            :label="$t('date')"
            :disabled="loading.load"
            hide-details
            color="white"
          />
        </v-col>
        <v-col cols="auto" lg="2">
          <autocomplete
            :label="$t('shifts')"
            v-model="selectedShifts"
            :items="availableShifts"
            multiple
            hide-details
            color="white"
          />
        </v-col>
        <v-col cols="auto" lg="3" v-if="allMeals.length">
          <autocomplete
            :label="$t('meals')"
            v-model="selectedMeals"
            :items="allMeals"
            item-text="text"
            item-value="value"
            multiple
            select-all
            hide-details
            color="white"
          />
        </v-col>
      </template>
    </page-toolbar>

    <mobile-filters
      v-if="device.isMobile"
      :filters="mobileFilterConfig"
      @update:filter="handleMobileFilterUpdate"
    />

    <div class="mt-3 px-2">
      <v-row
        v-if="loading.load"
        justify="center"
        align="center"
        style="min-height: 300px"
      >
        <v-col cols="auto">
          <v-progress-circular
            indeterminate
            size="64"
            color="primary"
          ></v-progress-circular>
        </v-col>
      </v-row>

      <empty-state
        v-else-if="!items.length"
        icon="mdi-food-off"
        :title="$t('no_data_for_selected_filters')"
        :subtitle="$t('no_recipes_for_selected_period')"
      />
      <v-row no-gutters>
        <v-col
          cols="12"
          lg="6"
          style="padding: 2px"
          v-for="(item, index) in allItemsWithSummary"
          :key="`section-${index}`"
        >
          <!-- Data Table -->
          <recipe-table
            :item="item"
            :all-items="items"
            @recalculate-item="recalculateItem"
          />
        </v-col>
      </v-row>
    </div>

    <!-- Mobile FAB Menu -->
    <v-speed-dial
      v-if="device.isMobile"
      v-model="fabMenu"
      fixed
      right
      direction="bottom"
      transition="slide-y-reverse-transition"
      style="top: 80px"
    >
      <template v-slot:activator>
        <v-btn v-model="fabMenu" color="grey" fab dark small depressed>
          <v-icon v-if="fabMenu">mdi-close</v-icon>
          <v-icon v-else>mdi-dots-vertical</v-icon>
        </v-btn>
      </template>

      <!-- Excel Button -->
      <v-btn
        fab
        small
        color="green darken-2"
        dark
        @click="exportExcel"
        :disabled="!items.length"
      >
        <v-icon>mdi-microsoft-excel</v-icon>
      </v-btn>

      <!-- Print Button -->
      <v-btn fab small color="primary" dark @click="print" :disabled="!items.length">
        <v-icon>mdi-printer</v-icon>
      </v-btn>

      <!-- Refresh Button -->
      <v-btn fab small color="blue" dark @click="load">
        <v-icon>mdi-reload</v-icon>
      </v-btn>
    </v-speed-dial>
  </div>
</template>

<script>
import moment from "moment";
import warehouseService from "@/services/warehouse";
import companyService from "@/services/company";
import warehouseFiltersMixin from "@/mixins/warehouse/warehouseFiltersMixin";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import PageHeader from "@/components/global/PageHeader.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";
import EmptyState from "@/components/global/EmptyState.vue";
import GlobalButtonExcel from "@/components/global/buttons/GlobalButtonExcel.vue";
import GlobalButtonPrint from "@/components/global/buttons/GlobalButtonPrint.vue";
import MobileFilters from "@/components/global/MobileFilters.vue";
import RecipeTable from "@/components/recipes/RecipeTable.vue";

export default {
  components: {
    PageHeader,
    PageToolbar,
    EmptyState,
    GlobalButtonExcel,
    GlobalButtonPrint,
    MobileFilters,
    RecipeTable,
  },
  mixins: [warehouseFiltersMixin, hasPrinting, hasExcel],
  data() {
    const today = new Date().toISOString().substr(0, 10);
    return {
      fabMenu: false,
      dateRange: [today, today],
      loading: {
        load: false,
        companies: false,
      },
      selectedLayout: null,
      availableShifts: [1, 2, 3, 4],
      selectedShifts: [1, 2, 3],
      allItems: [],
      selectedMeals: [],
    };
  },
  mounted() {
    this.loadCompanies();
  },
  methods: {
    async loadCompanies() {
      this.loading.companies = true;
      try {
        const res = await companyService.getCompanies();
        const companies = res.data || [];

        // Count companies by type
        const groupCount = companies.filter((c) => c.type === "group").length;
        const nonGroupCount = companies.length - groupCount;

        // Set default layout based on majority type
        if (groupCount > nonGroupCount) {
          this.selectedLayout = { text: this.$t("meals_in_meals"), value: "daily-group" };
        } else {
          this.selectedLayout = { text: this.$t("meals_in_meals"), value: "daily" };
        }

        // Load data after setting layout
        this.load();
      } catch (error) {
        console.error("Error loading companies:", error);
        // Set default on error
        this.selectedLayout = { text: this.$t("meals_in_meals"), value: "daily" };
        this.load();
      } finally {
        this.loading.companies = false;
      }
    },
    async load() {
      if (this.loading.load) {
        console.warn("Load operation already in progress, skipping duplicate request");
        return;
      }

      this.loading.load = true;

      try {
        let res;
        if (this.selectedLayout.value === "daily") {
          res = await warehouseService.getDailyWarehouseReport(104, {
            date_from: moment(this.dateRange[0]).format("YYYY-MM-DD"),
            date_to: moment(this.dateRange[1]).format("YYYY-MM-DD"),
          });
        } else {
          res = await warehouseService.getDailyGroupWarehouseReport(
            this.$store.state.restaurant.id,
            {
              date_from: moment(this.dateRange[0]).format("YYYY-MM-DD"),
              date_to: moment(this.dateRange[1]).format("YYYY-MM-DD"),
            }
          );
        }
        this.allItems = res.data;

        // Set all meals as selected by default (including summary)
        if (this.selectedMeals.length === 0) {
          this.selectedMeals = [
            ...this.allItems.map((item, index) => item.food_id || item.id || index),
            "summary", // Add summary to default selection
          ];
        }
      } catch (error) {
        console.error("Error loading warehouse report:", error);
      } finally {
        this.loading.load = false;
      }
    },

    recalculateItem(item) {
      // Force re-render by updating the item reference
      // This will trigger the computed properties for totals
      this.$forceUpdate();
    },
    getTotalShiftQuantity(shifts) {
      if (!shifts || !shifts.length) return "";
      return shifts.reduce((sum, shift) => sum + shift.quantity, 0);
    },
    calculateGroceryTotal(grocery, shifts, measure) {
      if (!shifts || !shifts.length) return "";
      const totalShiftQuantity = shifts.reduce((sum, shift) => sum + shift.quantity, 0);
      if (totalShiftQuantity <= 0) return "";
      const result = grocery.quantity * totalShiftQuantity;
      const formattedResult =
        measure && measure.toLowerCase() === "kom"
          ? Math.round(result)
          : result.toFixed(3);
      return formattedResult + measure;
    },
    handleMobileFilterUpdate({ key, value }) {
      switch (key) {
        case "view":
          // Find the full object from showItems based on value
          this.selectedLayout =
            this.showItems.find((item) => item.value === value) || null;
          break;
        case "dateRange":
          this.dateRange = value;
          break;
        case "shifts":
          this.selectedShifts = value;
          break;
        case "meals":
          this.selectedMeals = value;
          break;
      }
    },
    print() {
      this.setPrinter();

      // Create separate table for each meal
      const tables = [];

      this.items.forEach((item) => {
        if (!item.groceries || !item.groceries.length) return;

        // Generate headers with actual shift quantities for this item
        const headers = [
          { text: this.$t("grocery") || "Namirnica", value: "name", print: true },
          ...this.uniqueShifts.map((shift) => {
            const shiftData = item.shifts?.find((s) => s.shift === shift);
            const quantity = shiftData ? shiftData.quantity : 0;
            return {
              text: `${this.$t(`shift_${shift}`)} (${quantity})`,
              value: `shift_${shift}`,
              print: true,
            };
          }),
          { text: this.$t("total") || "Ukupno", value: "total", print: true },
        ];

        // Prepare grocery items for this meal
        const groceryItems = item.groceries.map((grocery) => {
          const groceryRow = {
            name: grocery.name,
            total: this.calculateGroceryTotal(grocery, item.shifts, grocery.measure),
          };

          this.uniqueShifts.forEach((shiftNum) => {
            const shift = item.shifts?.find((s) => s.shift === shiftNum);
            if (shift && shift.quantity > 0) {
              const quantity = grocery.quantity * shift.quantity;
              groceryRow[`shift_${shiftNum}`] =
                grocery.measure && grocery.measure.toLowerCase() === "kom"
                  ? Math.round(quantity)
                  : quantity.toFixed(3);
            } else {
              groceryRow[`shift_${shiftNum}`] = "";
            }
          });

          return groceryRow;
        });

        // Add table for this meal
        tables.push({
          title: item.food_name || "",
          headers,
          items: groceryItems,
        });
      });

      // Add summary table if exists
      if (this.summaryGroceries.length > 0) {
        // For summary table, calculate total quantities per shift across all meals
        const summaryHeaders = [
          { text: this.$t("grocery") || "Namirnica", value: "name", print: true },
          ...this.uniqueShifts.map((shift) => {
            // Calculate total quantity for this shift across all meals
            let totalShiftQuantity = 0;
            this.items.forEach((item) => {
              const shiftData = item.shifts?.find((s) => s.shift === shift);
              if (shiftData) totalShiftQuantity += shiftData.quantity;
            });
            return {
              text: `${this.$t(`shift_${shift}`)} (${totalShiftQuantity})`,
              value: `shift_${shift}`,
              print: true,
            };
          }),
          { text: this.$t("total") || "Ukupno", value: "total", print: true },
        ];

        const summaryItems = this.summaryGroceries.map((grocery) => {
          const summaryRow = {
            name: grocery.name,
            total:
              grocery.total > 0
                ? (grocery.measure && grocery.measure.toLowerCase() === "kom"
                    ? Math.round(grocery.total)
                    : grocery.total.toFixed(3)) + grocery.measure
                : "",
          };

          this.uniqueShifts.forEach((shiftNum) => {
            const shiftTotal = grocery.shifts?.[shiftNum] || 0;
            summaryRow[`shift_${shiftNum}`] =
              shiftTotal > 0
                ? grocery.measure && grocery.measure.toLowerCase() === "kom"
                  ? Math.round(shiftTotal)
                  : shiftTotal.toFixed(3)
                : "";
          });

          return summaryRow;
        });

        tables.push({
          title: this.$t("total") || "UKUPNO",
          headers: summaryHeaders,
          items: summaryItems,
        });
      }

      this.printDocument({
        pageTitle: this.$t("recipes") || "Recepti",
        title: this.$t("recipes") || "Recepti",
        subtitle: `${this.dateRange[0]} - ${this.dateRange[1]}`,
        tables,
      });
    },
    exportExcel() {
      this.setExcel();

      // Create separate sheet for each meal
      const sheets = [];

      this.items.forEach((item, index) => {
        if (!item.groceries || !item.groceries.length) return;

        // Generate headers with actual shift quantities for this item
        const headers = [
          { text: this.$t("grocery") || "Namirnica", value: "name", print: true },
          ...this.uniqueShifts.map((shift) => {
            const shiftData = item.shifts?.find((s) => s.shift === shift);
            const quantity = shiftData ? shiftData.quantity : 0;
            return {
              text: `${this.$t(`shift_${shift}`)} (${quantity})`,
              value: `shift_${shift}`,
              print: true,
            };
          }),
          { text: this.$t("total") || "Ukupno", value: "total", print: true },
        ];

        // Prepare grocery items for this meal
        const groceryItems = item.groceries.map((grocery) => {
          const groceryRow = {
            name: grocery.name,
            total: this.calculateGroceryTotal(grocery, item.shifts, grocery.measure),
          };

          this.uniqueShifts.forEach((shiftNum) => {
            const shift = item.shifts?.find((s) => s.shift === shiftNum);
            if (shift && shift.quantity > 0) {
              const quantity = grocery.quantity * shift.quantity;
              groceryRow[`shift_${shiftNum}`] =
                grocery.measure && grocery.measure.toLowerCase() === "kom"
                  ? Math.round(quantity)
                  : quantity.toFixed(3);
            } else {
              groceryRow[`shift_${shiftNum}`] = "";
            }
          });

          return groceryRow;
        });

        // Add sheet for this meal
        const sheetName = (item.food_name || `Jelo ${index + 1}`).substring(0, 31); // Excel sheet name limit
        sheets.push({
          name: sheetName,
          title: item.food_name || "",
          headers,
          items: groceryItems,
        });
      });

      // Add summary sheet if exists
      if (this.summaryGroceries.length > 0) {
        // For summary sheet, calculate total quantities per shift across all meals
        const summaryHeaders = [
          { text: this.$t("grocery") || "Namirnica", value: "name", print: true },
          ...this.uniqueShifts.map((shift) => {
            // Calculate total quantity for this shift across all meals
            let totalShiftQuantity = 0;
            this.items.forEach((item) => {
              const shiftData = item.shifts?.find((s) => s.shift === shift);
              if (shiftData) totalShiftQuantity += shiftData.quantity;
            });
            return {
              text: `${this.$t(`shift_${shift}`)} (${totalShiftQuantity})`,
              value: `shift_${shift}`,
              print: true,
            };
          }),
          { text: this.$t("total") || "Ukupno", value: "total", print: true },
        ];

        const summaryItems = this.summaryGroceries.map((grocery) => {
          const summaryRow = {
            name: grocery.name,
            total:
              grocery.total > 0
                ? (grocery.measure && grocery.measure.toLowerCase() === "kom"
                    ? Math.round(grocery.total)
                    : grocery.total.toFixed(3)) + grocery.measure
                : "",
          };

          this.uniqueShifts.forEach((shiftNum) => {
            const shiftTotal = grocery.shifts?.[shiftNum] || 0;
            summaryRow[`shift_${shiftNum}`] =
              shiftTotal > 0
                ? grocery.measure && grocery.measure.toLowerCase() === "kom"
                  ? Math.round(shiftTotal)
                  : shiftTotal.toFixed(3)
                : "";
          });

          return summaryRow;
        });

        sheets.push({
          name: "UKUPNO",
          title: this.$t("total") || "UKUPNO",
          headers: summaryHeaders,
          items: summaryItems,
        });
      }

      const fileName = `recepti_${this.dateRange[0]}_${this.dateRange[1]}.xlsx`;

      this.downloadExcel({
        fileName,
        sheets,
      });
    },
  },
  watch: {
    dateRange: {
      handler() {
        if (this.selectedLayout) {
          this.load();
        }
      },
      deep: true,
    },
    selectedLayout() {
      if (this.selectedLayout) {
        this.load();
      }
    },
  },
  computed: {
    items() {
      let filteredItems = this.allItems;

      // Filter by selected meals (excluding summary for now)
      if (this.selectedMeals && this.selectedMeals.length > 0) {
        // Filter out "summary" from selectedMeals for regular items filtering
        const regularMealIds = this.selectedMeals.filter((id) => id !== "summary");

        if (regularMealIds.length > 0) {
          filteredItems = filteredItems.filter((item, index) => {
            const id = item.food_id || item.id || index;
            return regularMealIds.includes(id);
          });
        } else {
          // If only summary is selected, show no regular items
          filteredItems = [];
        }
      }

      // Filter shifts
      if (this.selectedShifts && this.selectedShifts.length > 0) {
        filteredItems = filteredItems.map((item) => {
          const filteredItem = { ...item };

          // Filter shifts array to only include selected shifts
          if (item.shifts && item.shifts.length) {
            filteredItem.shifts = item.shifts
              .filter((shift) => this.selectedShifts.includes(shift.shift))
              .sort((a, b) => a.shift - b.shift); // Sort shifts by shift number
          }

          return filteredItem;
        });
      } else {
        // Even when not filtering, ensure shifts are sorted
        filteredItems = filteredItems.map((item) => {
          const filteredItem = { ...item };
          if (item.shifts && item.shifts.length) {
            filteredItem.shifts = [...item.shifts].sort((a, b) => a.shift - b.shift);
          }
          return filteredItem;
        });
      }

      return filteredItems;
    },
    //All groceries summary across all items
    summaryGroceries() {
      const groceryMap = new Map();

      // Aggregate all groceries across all items
      this.items.forEach((item) => {
        if (item.groceries && item.groceries.length) {
          item.groceries.forEach((grocery) => {
            const key = grocery.name + "_" + grocery.measure;

            if (!groceryMap.has(key)) {
              groceryMap.set(key, {
                id: grocery.id,
                name: grocery.name,
                measure: grocery.measure,
                shifts: {},
                total: 0,
              });

              // Initialize shifts
              this.uniqueShifts.forEach((shift) => {
                groceryMap.get(key).shifts[shift] = 0;
              });
            }

            const summaryGrocery = groceryMap.get(key);

            // Add quantities for each shift
            if (item.shifts && item.shifts.length) {
              item.shifts.forEach((shift) => {
                const quantity = grocery.quantity * shift.quantity;
                summaryGrocery.shifts[shift.shift] += quantity;
                summaryGrocery.total += quantity;
              });
            }
          });
        }
      });

      return Array.from(groceryMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    },
    allItemsWithSummary() {
      const result = [...this.items];

      // Only add summary if "summary" is selected in meals filter
      const isSummarySelected =
        this.selectedMeals && this.selectedMeals.includes("summary");

      if (isSummarySelected && this.summaryGroceries.length > 0) {
        // Create summary item with same structure as regular items
        const summaryItem = {
          isTotal: true,
          food_name: this.$t("total") || "UKUPNO",
          shifts: this.uniqueShifts.map((shift) => ({ shift, quantity: 0 })),
          groceries: this.summaryGroceries,
        };

        result.push(summaryItem);
      }

      return result;
    },
    uniqueShifts() {
      const shiftsSet = new Set();
      this.items.forEach((item) => {
        if (item.shifts && item.shifts.length) {
          item.shifts.forEach((shift) => {
            shiftsSet.add(shift.shift);
          });
        }
      });
      return Array.from(shiftsSet).sort((a, b) => a - b);
    },
    allMeals() {
      const mealsMap = new Map();

      // Add all regular meals
      this.allItems.forEach((item, index) => {
        if (item.food_name) {
          const id = item.food_id || item.id || index;
          mealsMap.set(id, {
            text: this.toLatin(item.food_name),
            value: id,
          });
        }
      });

      // Add summary as a special meal option
      mealsMap.set("summary", {
        text: this.$t("total") || "UKUPNO",
        value: "summary",
      });

      const meals = Array.from(mealsMap.values()).sort((a, b) =>
        a.text.localeCompare(b.text)
      );

      return meals;
    },
    mobileFilterConfig() {
      return {
        view: {
          label: this.$t("view"),
          value: this.selectedLayout ? this.selectedLayout.value : null,
          chipText: this.selectedLayout ? this.selectedLayout.text : "",
          type: "dropdown",
          items: this.showItems,
          itemText: "text",
          itemValue: "value",
          visible: true,
          clearable: false,
        },
        dateRange: {
          label: this.$t("date"),
          value: this.dateRange,
          chipText:
            this.dateRange && this.dateRange.length === 2
              ? `${this.dateRange[0]} - ${this.dateRange[1]}`
              : "",
          type: "date",
          visible: true,
          clearable: false,
        },
        shifts: {
          label: this.$t("shifts"),
          value: this.selectedShifts,
          chipText: `${this.selectedShifts.length} ${this.$t("shifts").toLowerCase()}`,
          type: "autocomplete",
          items: this.availableShifts,
          multiple: true,
          visible: true,
          clearable: false,
        },
        meals: {
          label: this.$t("meals"),
          value: this.selectedMeals,
          chipText: `${this.selectedMeals.length} ${this.$t("meals").toLowerCase()}`,
          type: "autocomplete",
          items: this.allMeals,
          itemText: "text",
          itemValue: "value",
          multiple: true,
          visible: this.allMeals.length > 0,
          clearable: false,
        },
      };
    },
  },
};
</script>
